import type { PullRequestDetail } from "@cmmd-forge/contracts";
import type { ReactNode } from "react";
import {
  CircleCheckIcon,
  CircleDashedIcon,
  CircleXIcon,
  MessageSquareIcon,
  TagIcon,
  UsersIcon,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { usePullRequestHandoffController } from "~/pullRequest/handoff/PullRequestHandoffProvider";

import {
  keyedPullRequestChecks,
  pullRequestStateLabel,
  reviewerEntries as createReviewerEntries,
  type PullRequestReviewerEntry,
} from "./pullRequestSummary.logic";

export function PullRequestSummaryHeader({ detail }: { readonly detail: PullRequestDetail }) {
  const stateVariant =
    detail.state === "merged" ? "success" : detail.state === "closed" ? "error" : "info";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={stateVariant}>{pullRequestStateLabel(detail.state, detail.isDraft)}</Badge>
      <span className="text-xs text-muted-foreground">
        {detail.repository} #{detail.number}
      </span>
    </div>
  );
}

export function PullRequestSummaryStatus({
  author,
  autoMergeEnabled,
  baseComparison,
  mergeabilityLabel,
}: {
  readonly author: PullRequestDetail["author"];
  readonly autoMergeEnabled: boolean | undefined;
  readonly baseComparison: string | null;
  readonly mergeabilityLabel: string;
}) {
  return (
    <>
      <PullRequestSummaryStatusLine
        mergeabilityLabel={mergeabilityLabel}
        baseComparison={baseComparison}
        autoMergeLabel={autoMergeLabel(autoMergeEnabled)}
      />
      <PullRequestSummaryAuthor author={author} />
    </>
  );
}

function PullRequestSummaryStatusLine({
  mergeabilityLabel,
  baseComparison,
  autoMergeLabel,
}: {
  readonly mergeabilityLabel: string;
  readonly baseComparison: string | null;
  readonly autoMergeLabel: string | null;
}) {
  return (
    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
      <span>{mergeabilityLabel}</span>
      {baseComparison !== null ? <span>{baseComparison}</span> : null}
      {autoMergeLabel !== null ? <span>{autoMergeLabel}</span> : null}
    </div>
  );
}

function PullRequestSummaryAuthor({ author }: { readonly author: PullRequestDetail["author"] }) {
  return author !== null ? (
    <p className="text-sm text-muted-foreground">Opened by {author.login}</p>
  ) : null;
}

export function PullRequestSummaryChecks({
  checks,
}: {
  readonly checks: PullRequestDetail["checks"];
}) {
  return (
    <section aria-label="Checks">
      {checks.length > 0 ? <CheckList checks={checks} /> : <NoChecks />}
    </section>
  );
}

export function PullRequestSummaryReviewers({
  reviewers,
  reviewerEntries,
  action,
}: {
  readonly reviewers: PullRequestDetail["reviewers"];
  readonly reviewerEntries?: ReadonlyArray<PullRequestReviewerEntry>;
  readonly action?: ReactNode;
}) {
  const entries = reviewerEntries ?? createReviewerEntries(reviewers, [], []);
  return (
    <section className="flex min-w-0 items-center gap-2 text-xs" aria-label="Reviewers">
      <UsersIcon className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="shrink-0 font-medium">Reviewers</span>
      {entries.length === 0 ? (
        <span className="text-muted-foreground">None</span>
      ) : (
        <span className="flex min-w-0 items-center -space-x-1">
          {entries.map((entry) => (
            <Tooltip key={entry.key}>
              <TooltipTrigger
                render={
                  <span
                    className={`inline-flex size-5 items-center justify-center overflow-hidden rounded-full bg-muted text-[9px] ring-2 ring-background ${reviewerVerdictClassName(entry)}`}
                  >
                    {entry.actor?.avatarUrl === null || entry.actor === null ? (
                      (entry.actor?.login ?? "?").slice(0, 1).toUpperCase()
                    ) : (
                      <img
                        src={entry.actor.avatarUrl}
                        alt={entry.actor.login}
                        className="size-full object-cover"
                      />
                    )}
                  </span>
                }
              />
              <TooltipPopup side="bottom">{reviewerTooltip(entry)}</TooltipPopup>
            </Tooltip>
          ))}
        </span>
      )}
      {action}
    </section>
  );
}

function reviewerVerdictClassName(entry: PullRequestReviewerEntry): string {
  if (entry.verdict === "approved") return entry.stale ? "ring-muted-foreground" : "ring-success";
  if (entry.verdict === "changes-requested")
    return entry.stale ? "ring-muted-foreground" : "ring-destructive";
  return "";
}

function reviewerTooltip(entry: PullRequestReviewerEntry): string {
  const login = entry.actor?.name ?? entry.actor?.login ?? "Deleted reviewer";
  if (entry.verdict === null) return login;
  const verdict = entry.verdict === "approved" ? "Approved" : "Changes requested";
  return entry.stale ? `${login} — ${verdict} earlier changes` : `${login} — ${verdict}`;
}

export function PullRequestSummaryLabels({
  labels,
}: {
  readonly labels: PullRequestDetail["labels"];
}) {
  return (
    <section className="flex min-w-0 items-start gap-2 text-xs" aria-label="Labels">
      <TagIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <span className="shrink-0 font-medium">Labels</span>
      <span className="flex min-w-0 flex-wrap items-center gap-1">
        {labels.map((label) => (
          <span
            key={label.name}
            className="inline-flex max-w-48 items-center gap-1.5 rounded-full border border-border/70 bg-muted/40 py-0.5 pl-1.5 pr-2"
          >
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full bg-muted-foreground"
              {...(label.color === null
                ? {}
                : { style: { backgroundColor: `#${label.color.replace(/^#/u, "")}` } })}
            />
            <span className="truncate">{label.name}</span>
          </span>
        ))}
      </span>
    </section>
  );
}

export function PullRequestSummaryComments({ count }: { readonly count: number }) {
  return (
    <section className="flex min-w-0 items-center gap-2 text-xs" aria-label="Comments">
      <MessageSquareIcon className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="shrink-0 font-medium">Comments</span>
      <span className="text-muted-foreground">
        {count} {count === 1 ? "comment" : "comments"}
      </span>
    </section>
  );
}

function CheckList({ checks }: { readonly checks: PullRequestDetail["checks"] }) {
  return (
    <ul className="mt-2 space-y-0.5">
      {keyedPullRequestChecks(checks).map(({ check, key }) => (
        <li
          key={key}
          className="group flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent/60"
        >
          <CheckStatusIcon status={check.status} />
          <span className="min-w-0 truncate">{check.name}</span>
          <span className="ml-auto shrink-0 text-muted-foreground">{check.status}</span>
          <CheckFixAction check={check} />
        </li>
      ))}
    </ul>
  );
}

function CheckStatusIcon({ status }: { readonly status: string }) {
  if (status === "success") {
    return (
      <CircleCheckIcon aria-label="Passed" className="size-3.5 shrink-0 text-success-foreground" />
    );
  }
  if (status === "failure" || status === "cancelled") {
    return (
      <CircleXIcon aria-label="Failed" className="size-3.5 shrink-0 text-destructive-foreground" />
    );
  }
  return (
    <CircleDashedIcon aria-label="Pending" className="size-3.5 shrink-0 text-muted-foreground" />
  );
}

function CheckFixAction({ check }: { readonly check: PullRequestDetail["checks"][number] }) {
  const handoff = usePullRequestHandoffController();
  if (handoff === null) return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      disabled={handoff.pendingAction !== null}
      onClick={() => void handoff.run("fix-finding", { kind: "check", check })}
    >
      {handoff.currentThreadMatches ? "Fix here" : "Fix"}
    </Button>
  );
}

function NoChecks() {
  return <p className="text-xs text-muted-foreground">No checks reported.</p>;
}

function autoMergeLabel(autoMergeEnabled: boolean | undefined): string | null {
  if (autoMergeEnabled === undefined) return null;
  return autoMergeEnabled ? "Auto-merge enabled" : "Auto-merge disabled";
}
